import toast from 'react-hot-toast'

// Piezas sin JSX que comparten las dos páginas financieras
// (`ReceivablesPage`, `PayablesPage`). Viven en un `.js` y no en un `.jsx` a
// propósito: `react-refresh/only-export-components` avisa de cualquier export
// que no sea un componente, y estas no lo son.

// Los estilos responsive de las dos páginas. Se inyectan con un <style> en
// cada una; son idénticos, así que la cadena es una sola.
//
// `.fin-modal-grid-2` estaba aquí sin un solo call site en toda la página
// original — se ha eliminado al partirla en vez de copiarse dos veces.
export const RESPONSIVE_CSS = `
@media (max-width: 1100px) {
  .fin-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
}
@media (max-width: 768px) {
  .fin-stats-grid { grid-template-columns: 1fr 1fr !important; }
  .fin-grid { grid-template-columns: 1fr !important; }
  .fin-card-metrics { grid-template-columns: 1fr 1fr !important; }
  .fin-card-actions { flex-direction: column !important; }
  .fin-card-actions .btn { width: 100% !important; }
  .fin-modal { width: 100% !important; max-width: 100% !important; }
}
@media (max-width: 768px) {
  .fin-input { font-size: 16px !important; }
}
`

// El signo va DELANTE del símbolo: '-$1,430.89', no '$-1,430.89'. Es la
// convención en inglés, y dejó de ser un detalle cosmético cuando
// `corporate/overview` empezó a devolver `allocation.cash_excess`, que es
// negativo en el caso normal — la empresa cierra el mes en déficit hoy, así
// que esa cifra sale en cada carga de Company finances.
//
// El signo se decide sobre la cifra YA formateada, no sobre `n < 0`: un
// -0.001 redondea a 0.00 y '-$0.00' afirmaría un negativo que no queda en
// pantalla. `Number(-0) < 0` ya es false, así que el cero negativo sale bien
// por sí solo.
export const money = (v) => {
  const n = Number(v || 0)
  const formatted = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })
  return `${n < 0 && formatted !== '0.00' ? '-' : ''}$${formatted}`
}

export const todayISO = () => new Date().toISOString().slice(0, 10)

export const fmtDateTime = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString()
}

// Las fechas de corte (`as_of`) llegan como 'YYYY-MM-DD'. new Date('2025-11-30')
// se interpreta como medianoche UTC y al formatearla en cualquier huso al oeste
// retrocede un día. Partimos la cadena a mano: una fecha de corte es un dato
// del backend, no un instante, y no puede moverse según dónde esté el navegador.
const fmtCalendarDay = (ymd, options) => {
  if (!ymd) return ''
  const [y, m, d] = String(ymd).split('-').map(Number)
  if (!y || !m || !d) return String(ymd)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', options)
}

// "Oct 1, 2026"
export const fmtDay = (ymd) => fmtCalendarDay(ymd, { year: 'numeric', month: 'short', day: 'numeric' })

// "Oct 7": para ejes y tooltips de gráficas diarias, donde el año ya lo da el
// rango. Mes con nombre y no "07/10", que un lector en EE. UU. lee como 10 de julio.
export const fmtShortDay = (ymd) => fmtCalendarDay(ymd, { month: 'short', day: 'numeric' })

// Un sobrepago no es un error: el movimiento se guardó. Significa que el
// saldo quedó en negativo, algo que el backend además registra como Event
// (FINANCIAL_OVERPAYMENT). Avisar aquí es lo que convierte un pago duplicado
// en algo que alguien nota el mismo día en vez de tres meses después.
export const warnIfOverpaid = (result, noun) => {
  if (!result?.overpaid) return
  toast(`${noun} saved, but it exceeds the balance by ${money(result.overpaid_by)}.`, {
    icon: '⚠️',
    duration: 7000,
  })
}

// Un saldo heredado es payable o receivable, nunca los dos: el backend contesta
// 422 LegacyKindMismatch si se intenta saldar un receivable con un pago o al
// revés. La UI no ofrece ese camino —ahora ni siquiera están en la misma
// página—, pero traducirlo aquí evita que, si alguna vez llega, quede como el
// genérico "Could not save" sin decir qué pasó. El envelope de error del
// backend es {"detail": "..."}; leemos también `code` por si el 422 lo trae
// estructurado.
export const KIND_MISMATCH = 'LegacyKindMismatch'

export const saveErrorMessage = (err, noun) => {
  const body = err?.response?.data
  const detail = typeof body?.detail === 'string' ? body.detail : null
  const code = body?.code ?? body?.error ?? null
  if (code === KIND_MISMATCH || detail?.includes(KIND_MISMATCH)) {
    return `A payment can only settle a payable, and a collection only a receivable. This ${noun} doesn't match the balance it was aimed at.`
  }
  return detail || `Could not save ${noun}`
}
